/**
 * AuthExpiredBanner.tsx
 *
 * Canonical auth-failure banner. Introduced in Slice E; expanded in Slice F
 * bugbash 2026-05-06 to handle BOTH AUTH_EXPIRED (time-bound) and AUTH_INVALID
 * (bad/wrong/revoked) under one component with kind-aware copy.
 * Supersedes the legacy SyncFailedReconnectBanner (brand guide §34.5).
 *
 * Trigger: `authExpiredEnvelope?.code` in Redux is one of AUTH_EXPIRED |
 *   AUTH_INVALID — Canvas.tsx routes both to this surface (slot is shared).
 * Dismissible: yes — user can dismiss; the Reconnect CTA remains accessible
 *   via the Sync button (Canvas.tsx preserves the "Sync failed" CTA in that path).
 * Navigation: "Reconnect CampMinder" triggers the in-flow HarvestModal via
 *   the `onReconnect` prop (same pattern as SyncFailedReconnectBanner).
 *
 * Stacking (CEO-locked rule per plan §5.4 #4):
 *   - Renders below RateLimitBanner when both are simultaneously active.
 *   - Both banners are ALWAYS visible — no banner hides another.
 *
 * Layout: 3-row column layout per mockup 2026-05-05-auth-expired-banner-v1.html:
 *   Row 1 — icon + headline + solid Reconnect CTA (right-aligned)
 *   Row 2 — body copy (indented to align with headline text after icon)
 *   Row 3 — ghost Dismiss button. (The legacy "Manage credentials →" link
 *           was removed in Slice F bugbash 2026-05-06; the Reconnect CTA
 *           is the canonical recovery path. See in-line comment at row 3.)
 *
 * Brand guide §34.5. Mockup: 2026-05-05-auth-expired-banner-v1.html.
 *
 * Accessibility (brand guide §34.10):
 *   - role="alert" — assertive; user action required.
 *   - aria-label on wrapper describes failure context.
 *   - Reconnect CTA: aria-label="Reconnect CampMinder — re-authenticate"
 *   - Dismiss: aria-label="Dismiss — banner can be reopened"
 *   - focus-visible:ring-focusRing on all interactive elements.
 */

import React from 'react'
import { KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'

import type { SyncErrorCode } from '@/types/SyncError.types'

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AuthExpiredBannerProps {
  /**
   * Discriminator between time-expired and otherwise-invalid creds. Drives
   * the headline + body copy; CTA + icon + color are identical across both
   * variants. Defaults to `'AUTH_EXPIRED'` so older callers that don't pass
   * an envelope code keep the prior behaviour.
   *
   * Slice F bugbash 2026-05-06.
   */
  code?: Extract<SyncErrorCode, 'AUTH_EXPIRED' | 'AUTH_INVALID'>
  /** Called when user clicks "Reconnect CampMinder". Must reopen HarvestModal. */
  onReconnect: () => void
  /** Called when user clicks the Dismiss button. */
  onDismiss: () => void
}

// ─── Component ────────────────────────────────────────────────────────────────

const COPY: Record<
  Extract<SyncErrorCode, 'AUTH_EXPIRED' | 'AUTH_INVALID'>,
  { ariaLabel: string; body: string; headline: string }
> = {
  AUTH_EXPIRED: {
    ariaLabel: 'Sync failed — CampMinder credentials expired',
    body:
      'Your CampMinder API key expired. Reconnect to resume syncing your dashboards.',
    headline: 'CampMinder credentials expired.',
  },
  AUTH_INVALID: {
    ariaLabel:
      'Sync failed — CampMinder credentials not working',
    body:
      "Your CampMinder API key isn't working. Verify it's correct in your CampMinder account, or reconnect with a new key.",
    headline: 'CampMinder credentials not working.',
  },
}

export const AuthExpiredBanner: React.FC<AuthExpiredBannerProps> = ({
  code = 'AUTH_EXPIRED',
  onReconnect,
  onDismiss,
}) => {
  const copy = COPY[code]
  return (
  <div
    className="flex flex-col gap-2.5 px-5 py-3.5 bg-destructive/5 border-b-2 border-destructive/35 flex-shrink-0"
    role="alert"
    aria-label={copy.ariaLabel}
    data-testid="auth-expired-banner"
  >
    {/* Row 1: icon + headline + primary CTA */}
    <div className="flex items-center gap-2.5 flex-wrap justify-between">
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        <KeyRound
          size={16}
          className="text-[hsl(0_63%_42%)] flex-shrink-0"
          aria-hidden="true"
        />
        <span className="text-[13px] font-semibold text-[hsl(0_63%_42%)] leading-snug">
          {copy.headline}
        </span>
      </div>
      {/* Primary CTA — solid red, forces attention */}
      <Button
        size="sm"
        variant="destructive"
        className="flex items-center gap-1.5 flex-shrink-0 font-semibold"
        onClick={onReconnect}
        aria-label="Reconnect CampMinder — re-authenticate"
        data-testid="btn-reconnect-campminder"
      >
        <KeyRound size={13} aria-hidden="true" />
        Reconnect CampMinder
      </Button>
    </div>

    {/* Row 2: body copy — indented to align with headline text */}
    <p className="text-[12px] text-[hsl(0_63%_42%/0.85)] leading-relaxed pl-[26px]">
      {copy.body}
    </p>

    {/* Row 3: dismiss only — Reconnect CampMinder is the canonical recovery
        path; the legacy "Manage credentials →" link was removed in Slice F
        bugbash 2026-05-06 because it redundantly leads to the same harvest
        flow Reconnect already opens, and a single CTA reduces visual noise. */}
    <div className="flex items-center gap-3 pl-[26px] flex-wrap">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="text-[hsl(0_63%_42%)] border-destructive/40 bg-transparent hover:bg-destructive/5 text-[12px] font-medium"
        aria-label="Dismiss — banner can be reopened"
        onClick={onDismiss}
        data-testid="btn-dismiss-auth-banner"
      >
        Dismiss
      </Button>
    </div>
  </div>
  )
}
